import {
  ApprovalGroup,
  ApprovalStatus,
} from "../../../core/models/entities/Approval";
import { useTranslation } from "react-i18next";
import { GetOverallApprovalStatus } from "../../../core/models/entities/BaseLogo";

export function ApprovalDisplay({
  approval,
  isOrder = false,
}: {
  approval: ApprovalGroup;
  isOrder?: boolean;
}) {
  const { t } = useTranslation();
  const { Vendor, Customer, External, Header } = buildStatusCSS(approval);

  return (
    <>
      <ApprovalStatusHeader status={Header.value} isOrder={isOrder} />
      {Vendor && (
        <div
          className={`font-semibold flex items-center space-x-2 ${Vendor.textColor}`}
        >
          <div
            className={`rounded-full h-[0.25rem] w-[0.25rem] ${Vendor.dotColor}`}
          />
          <p>{t("vendor")}</p>
        </div>
      )}
      {Customer && (
        <div
          className={`font-semibold flex items-center space-x-2 ${Customer.textColor}`}
        >
          <div
            className={`rounded-full h-[0.25rem] w-[0.25rem] ${Customer.dotColor}`}
          />
          <p>{t("customer")}</p>
        </div>
      )}
      {External && (
        <div
          className={`font-semibold flex items-center space-x-2 ${External.textColor}`}
        >
          <div
            className={`rounded-full h-[0.25rem] w-[0.25rem] ${External.dotColor}`}
          />
          <p>{t("compLogoDesign.endCustomer")}</p>
        </div>
      )}
    </>
  );
}

export function ApprovalListDisplay({
  approval,
  isOrder = false,
}: {
  approval: ApprovalGroup;
  isOrder?: boolean;
}) {
  const { t } = useTranslation();
  const { Vendor, Customer, External, Header } = buildStatusCSS(approval);
  // If no one has approved yet, fallback to "approved" text.
  if (!(Vendor || Customer || External)) {
    return (
      <p className="font-semibold text-xs uppercase text-status-success">
        {t("compLogoDesign.statusApproved")}
      </p>
    );
  }

  // If only vendor has approved and no other participants have any status, show simple "APPROVED"
  if (Vendor && !Customer && !External) {
    return (
      <p className="font-semibold text-xs uppercase text-status-success">
        {t("compLogoDesign.statusApproved")}
      </p>
    );
  }

  return (
    <>
      <ApprovalStatusHeader status={Header.value} isOrder={isOrder} />
      {Vendor && (
        <div
          className={`font-semibold flex items-center space-x-2 ${Vendor.textColor}`}
        >
          <div
            className={`rounded-full h-[0.25rem] w-[0.25rem] ${Vendor.dotColor}`}
          />
          <p>{t("vendor")}</p>
        </div>
      )}
      {Customer && (
        <div
          className={`font-semibold flex items-center space-x-2 ${Customer.textColor}`}
        >
          <div
            className={`rounded-full h-[0.25rem] w-[0.25rem] ${Customer.dotColor}`}
          />
          <p>{t("customer")}</p>
        </div>
      )}
      {External && (
        <div
          className={`font-semibold flex items-center space-x-2 ${External.textColor}`}
        >
          <div
            className={`rounded-full h-[0.25rem] w-[0.25rem] ${External.dotColor}`}
          />
          <p>{t("compLogoDesign.endCustomer")}</p>
        </div>
      )}
    </>
  );
}

export function ApprovalStatusHeader({
  status,
  isOrder = false,
}: {
  status: ApprovalStatus;
  isOrder?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <p
      className={`font-semibold text-xs uppercase ${statusColor(
        status,
        isOrder
      )}`}
    >
      {t(headerDisplayTextKey(status, isOrder))}
    </p>
  );
}

function headerDisplayTextKey(
  status: ApprovalStatus,
  isOrder: boolean = false
) {
  switch (status) {
    case ApprovalStatus.Pending:
      return "compLogoDesign.statusAwaiting";
    case ApprovalStatus.Approved:
      return "compLogoDesign.statusApprovedBy";
    case ApprovalStatus.Rejected:
      return "compLogoDesign.statusRejected";
  }
}

function statusColor(statusType: ApprovalStatus, isOrder: boolean = false) {
  switch (statusType) {
    case ApprovalStatus.Approved:
      return "text-status-success";
    case ApprovalStatus.Pending:
      return "text-status-pending-1";
    case ApprovalStatus.Rejected:
      return isOrder ? "text-status-failed-1" : "text-status-success";
  }
}

function buildStatusCSS(approval: ApprovalGroup) {
  const overAll = GetOverallApprovalStatus(approval);
  // approval?.: callers assert `logo.approval!`, but foreign-org rows
  // (admin/act-as lists) can lack the group — render "pending, no
  // participants" instead of throwing the list into RouteErrorView.
  return {
    Header: { value: overAll, textColor: sssHeader(overAll) },
    Vendor: approval?.vendor ? paticipantCSS(approval.vendor.status) : null,
    Customer: approval?.customer
      ? paticipantCSS(approval.customer.status)
      : null,
    External: approval?.external
      ? paticipantCSS(approval.external.status)
      : null,
  };
}

function sssHeader(statusType: ApprovalStatus) {
  switch (statusType) {
    case ApprovalStatus.Approved:
      return "text-status-success";
    case ApprovalStatus.Pending:
      return "text-status-pending-1";
    case ApprovalStatus.Rejected:
      return "text-status-failed-1";
  }
}

function paticipantCSS(logoStatus: ApprovalStatus) {
  switch (logoStatus) {
    case ApprovalStatus.Approved:
      return { textColor: "text-gray-600", dotColor: "bg-status-success" };
    case ApprovalStatus.Pending:
      return {
        textColor: "text-status-pending",
        dotColor: "bg-status-pending-1",
      };
    case ApprovalStatus.Rejected:
      return {
        textColor: "text-status-failed-1",
        dotColor: "bg-status-failed-1",
      };
  }
}
